import { AutocompleteComponent, IAutocompleteOptions } from '../classes/autocomplete';
import { buildLoader, isRestoredNavigation } from '../helpers/pageLoader';

const schoolSearchBlocks = [].slice.call(document.querySelectorAll('[data-school-search]'));

// A restored page brings back the state it was frozen in: the block still carries .has-results,
// which is what gives the panel its min-height, while the result list inside it is `hidden` because
// autocomplete.js closed it on selection — so the panel reads as an empty box. The browser also
// restores the term the user typed. Put each block back to how it looks on a fresh load. Keyed off
// the DOM rather than the component instances, because the entries share no webpack runtime and
// this also has to work when the school data fetch failed.
window.addEventListener('pageshow', (e) => {
    if (isRestoredNavigation(e)) {
        schoolSearchBlocks.forEach(resetSchoolSearchBlock);
    }
});

if (schoolSearchBlocks && schoolSearchBlocks.length > 0) {
    window.fetch('/api/schoolsearch/?term=')
        .then(response => {
            if (response.status !== 200) {
                throw "School search API fetch failed: " + response.status;
            } else {
                return response.text();
            }
        })
        .then(data => initializeAutocomplete(JSON.parse(data)));
}

function initializeAutocomplete(schoolData) {

    if (schoolData) {
        schoolSearchBlocks.forEach(block => {
            const blockID = block.getAttribute('data-school-search');
            const blockInput = block.querySelector('.autocomplete-input') as HTMLInputElement;
            const blockPanelContainer = block.querySelector('[data-school-search-results-panel]') as HTMLElement;
            const blockPanel = block.querySelector('[data-school-search-results]') as HTMLElement;
            const panelClose = block.querySelector('[data-school-search-close]') as HTMLButtonElement;
            const blockForm = block.querySelector('form') as HTMLFormElement;
            const profile = block.getAttribute('data-profile-search');

            const schoolSearchBlockOptions: IAutocompleteOptions = {
                input: blockInput,
                panelContainer: blockPanelContainer,
                resultsContainer: blockPanel,
                blockContainer: block,
                form: blockForm,
                submitCallback: () => {
                    const firstLink = blockPanel.querySelector('li:first-child a') as HTMLAnchorElement;
                    if (profile) {
                        const firstResult = blockPanel.querySelector('.autocomplete-results .autocomplete-result:first-child a:first-child') as HTMLAnchorElement;

                        // grab the first entry and go there
                        if (firstResult) {
                            buildLoader();
                            window.location.assign(firstResult.href);
                        }
                        return;
                    }
                    if (firstLink) {
                        buildLoader();
                        window.location.assign(firstLink.href);
                    }
                    else {
                        blockForm.submit();
                    }
                },
                keys: ["SchoolLongName", "AddressZipCode"],
                autocompleteSelector: `#Autocomplete_${blockID}`,
                autocompleteResultsSelector: `#Autocomplete_results_${blockID}`,
                resultFormat: (data, source) => {
                    const schoolName = data['key'] == 'SchoolLongName' ? data.match : data['value']['SchoolLongName'] as string;
                    const schoolUrl = profile ? `${profile}${data['value']['SchoolShortNameSearch']}` : data['value']['Url'] as string;
                    source.innerHTML = generatePageLink(schoolUrl, schoolName, data['value']['SchoolLongName']);
                    const link = source.querySelector('a');
                    link.addEventListener('click', (e) => {
                        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) {
                            return; // let the browser handle new-tab/new-window/download
                        }
                        buildLoader();
                    });
                    source.setAttribute('class', 'autocomplete-result');
                },
                sortFunction: (a, b) => {
                    const aSchool = a['value']['SchoolLongName'].toLowerCase();
                    const bSchool = b['value']['SchoolLongName'].toLowerCase();
                    const aStartsWith = a.match.indexOf('<') === 0;
                    const bStartsWith = b.match.indexOf('<') === 0;
                    if (!aStartsWith && bStartsWith) {
                        return 1;
                    }
                    else if (aStartsWith && !bStartsWith) {
                        return -1;
                    }
                    else {
                        return aSchool.localeCompare(bSchool);
                    }
                },
                dataSrc: schoolData,
                cache: true,
                closeButton: panelClose,
                uniqueId: blockID
            }
            new AutocompleteComponent(schoolSearchBlockOptions);
        });
    }
    else {
        schoolSearchBlocks.forEach(block => {
            const loader = block.querySelector('.loading-container');
            const input = block.querySelector('.autocomplete-input');
            if (loader && input) {
                loader.classList.remove('loading-container');
                input.removeAttribute('disabled');
            }
        })
        console.error("No school data found. The School Search form will now submit straight to the search page.");
    }
}

function resetSchoolSearchBlock(block: HTMLElement) {
    block.classList.remove('has-results');

    const panel = block.querySelector('[data-school-search-results]') as HTMLElement;
    if (panel) {
        // make sure the autocomplete library can control this
        panel.classList.remove('hide');

        // Empty the results list, but don't delete it
        const list = panel.querySelector('.autocomplete-results');
        if (list) {
            list.innerHTML = '';
        }
    }

    const input = block.querySelector('.autocomplete-input') as HTMLInputElement;
    if (input) {
        // blank out the input box
        input.value = '';
    }
}

function generatePageLink(schoolUrl: string, schoolName: string, query: string) {
    return `<a href="${schoolUrl}" class="autocomplete-result-link" tabindex="-1" data-query="${query}">${schoolName}</a>`;
}